import { useCallback, useEffect, useState } from 'react';
import { CircleAlert, CircleCheck, Database, RefreshCw } from 'lucide-react';
import {
  IMPORT_STATUS_LABEL,
  IMPORT_TRIGGER_LABEL,
  ImportStatus,
  UserRole,
  type ImportDivergenceDto,
  type ImportListDto,
  type ImportRunDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatDateTime } from '../../lib/format';
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '../../components/ui';

/**
 * `/admin/importacao` — última execução, contagens e divergências (PLAN §7,
 * tela 13).
 *
 * O percentual de "data pela digitação" aparece como **informação, não alerta**.
 * Metade ou mais é o normal: `D1_ZDTENTR` só é preenchida quando a digitação sai
 * da janela do prazo, então vazio significa digitação colada na entrega — que é
 * exatamente o caso em que a data de digitação é uma boa data de entrega.
 *
 * A primeira versão desta tela alertava acima de 50% e teria disparado já na
 * carga histórica, que deu 54%. Alerta que dispara na operação normal ensina a
 * ignorar alerta.
 */

const STATUS_TONE: Record<ImportStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  RUNNING: 'neutral',
  SUCCESS: 'success',
  PARTIAL: 'warning',
  FAILED: 'danger',
};

export function ImportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [data, setData] = useState<ImportListDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [divergences, setDivergences] = useState<ImportDivergenceDto[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<ImportListDto>('/imports'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar o histórico.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function reprocess() {
    setRunning(true);
    setError(null);
    try {
      await api.post('/imports/run', {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível reprocessar.');
    } finally {
      setRunning(false);
    }
  }

  async function toggleDivergences(run: ImportRunDto) {
    if (open === run.id) {
      setOpen(null);
      return;
    }
    setOpen(run.id);
    setDivergences(null);
    try {
      setDivergences(await api.get<ImportDivergenceDto[]>(`/imports/${run.id}/divergences`));
    } catch {
      setDivergences([]);
    }
  }

  const last = data?.runs[0] ?? null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Sincronização com o Protheus
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Roda sozinha de madrugada e traz <strong>tudo o que o portal lê do ERP</strong>: o
            cadastro dos monitorados e as compras dos últimos {data?.windowDays ?? 45} dias.
            Sincronizar de novo é seguro — a carga é idempotente e não duplica nada.
          </p>
        </div>
        {isAdmin && (
          <Button loading={running} onClick={() => void reprocess()}>
            <RefreshCw className="size-4" aria-hidden />
            Sincronizar agora
          </Button>
        )}
      </header>

      {error && <Alert>{error}</Alert>}

      {last && <LastRun run={last} />}

      {!data ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : data.runs.length === 0 ? (
        <EmptyState
          title="Nenhuma importação executada"
          description="A primeira roda no próximo horário agendado, ou agora pelo botão acima."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[56rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">Quando</th>
                <th className="px-4 py-3 font-medium">Situação</th>
                <th className="px-4 py-3 font-medium">Janela</th>
                <th className="px-4 py-3 text-right font-medium">Itens</th>
                <th className="px-4 py-3 text-right font-medium">Encerrados</th>
                <th className="px-4 py-3 text-right font-medium">Datas trocadas</th>
                <th className="px-4 py-3 text-right font-medium">Divergências</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.runs.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  expanded={open === run.id}
                  divergences={open === run.id ? divergences : null}
                  onToggle={() => void toggleDivergences(run)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LastRun({ run }: { run: ImportRunDto }) {
  const fallbackPct =
    run.deliveriesRead > 0 ? Math.round((run.deliveryDateFallback / run.deliveriesRead) * 100) : 0;

  if (run.status === ImportStatus.FAILED) {
    return (
      <Card className="border-red-200 bg-red-50/40">
        <div className="flex items-center gap-2">
          <CircleAlert className="size-4 text-red-600" aria-hidden />
          <h2 className="font-medium text-slate-900">A última sincronização falhou</h2>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {formatDateTime(run.startedAt)} — {run.errorMessage}
        </p>
        <p className="mt-2 text-sm text-slate-600">
          O portal continua servindo o que já foi importado. Nenhum número mudou.
        </p>
      </Card>
    );
  }

  const cards: { label: string; value: string; hint?: string }[] = [
    {
      label: 'Cadastros conferidos',
      value: run.suppliersRead.toLocaleString('pt-BR'),
      hint:
        run.suppliersUpdated > 0
          ? `${run.suppliersUpdated} com dado alterado`
          : 'nenhum dado mudou',
    },
    { label: 'Itens de pedido lidos', value: run.ordersRead.toLocaleString('pt-BR') },
    { label: 'Encerrados na janela', value: run.itemsClosed.toLocaleString('pt-BR') },
    { label: 'Data pela digitação', value: `${fallbackPct}%`, hint: 'normal acima de 50%' },
  ];

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.label}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{card.value}</p>
            {card.hint && <p className="mt-0.5 text-xs text-slate-400">{card.hint}</p>}
          </Card>
        ))}
      </div>

      {/* Cadastro fora e compras em dia: a execução não é um sucesso limpo, e a
          tela diz isso em vez de mostrar "concluída" escondendo metade. */}
      {run.status === ImportStatus.PARTIAL && (
        <Alert>
          As compras foram lidas, mas o cadastro no SA2 não: {run.errorMessage} Nada foi apagado —
          razão social, CNPJ e cidade seguem como estavam.
        </Alert>
      )}

      {run.suppliersMissing.length > 0 && (
        <Alert>
          <span className="block">
            {run.suppliersMissing.length === 1
              ? '1 loja monitorada não existe no cadastro do ERP nesta filial: '
              : `${run.suppliersMissing.length} lojas monitoradas não existem no cadastro do ERP nesta filial: `}
            <span className="font-mono text-xs">{run.suppliersMissing.join(', ')}</span>
          </span>
          <span className="mt-1 block">
            Nenhuma foi desligada. Ou o código está errado no portal, ou o fornecedor saiu da
            filial — as duas pedem uma conferência.
          </span>
        </Alert>
      )}

      {run.skippedByType > 0 && (
        <Alert>
          {run.skippedByType} linha(s) com pedido vinculado foram descartadas por não serem nota
          fiscal normal (devolução, complementar ou beneficiamento-remessa). Isso não acontecia no
          levantamento — vale conferir.
        </Alert>
      )}
    </div>
  );
}

function RunRow({
  run,
  expanded,
  divergences,
  onToggle,
}: {
  run: ImportRunDto;
  expanded: boolean;
  divergences: ImportDivergenceDto[] | null;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="transition-colors hover:bg-slate-50/70">
        <td className="px-4 py-3">
          <p className="text-slate-800">{formatDateTime(run.startedAt)}</p>
          <p className="text-xs text-slate-500">
            {IMPORT_TRIGGER_LABEL[run.trigger]}
            {run.triggeredByName && ` · ${run.triggeredByName}`}
            {run.durationMs !== null && ` · ${(run.durationMs / 1000).toFixed(1)}s`}
          </p>
        </td>
        <td className="px-4 py-3">
          <Badge tone={STATUS_TONE[run.status]}>
            {run.status === ImportStatus.SUCCESS && <CircleCheck className="size-3" aria-hidden />}
            {IMPORT_STATUS_LABEL[run.status]}
          </Badge>
        </td>
        <td className="px-4 py-3 text-xs text-slate-500">
          {formatDate(run.fromDate)} a {formatDate(run.toDate)}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{run.ordersRead}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{run.itemsClosed}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{run.dateChanges}</td>
        <td className="px-4 py-3 text-right">
          {run.divergences === 0 ? (
            <span className="text-slate-400">—</span>
          ) : (
            <button
              type="button"
              onClick={onToggle}
              className="font-medium text-brand-700 underline-offset-4 hover:underline"
            >
              {run.divergences}
            </button>
          )}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className="bg-slate-50/70 px-4 py-3">
            <p className="mb-2 flex items-center gap-2 text-sm text-slate-700">
              <Database className="size-4 text-slate-400" aria-hidden />
              O ERP passou a discordar destas competências, que já estão fechadas. As notas
              publicadas <strong>não</strong> mudaram — corrigir exige recalcular a competência,
              com motivo.
            </p>
            {!divergences ? (
              <Skeleton className="h-12" />
            ) : (
              <ul className="space-y-1 text-sm text-slate-600">
                {divergences.map((item) => (
                  <li key={item.id} className="flex flex-wrap gap-x-2">
                    <span className="font-mono text-xs text-slate-500">{item.competence}</span>
                    <span className="font-medium text-slate-800">{item.supplierName}</span>
                    <span className="font-mono text-xs text-slate-500">
                      pedido {item.orderNumber}/{item.orderItem}
                    </span>
                    <span>
                      {item.field}: {item.previousValue ?? '—'} → {item.newValue ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
